import React, { ReactNode, useEffect, useState } from "react";

import { omit } from "lodash";
import isEqual from "lodash/isEqual";
import { useSelector } from "react-redux";

import { CenteredGreedy } from "components/Layout";
import LoadingSpinner from "components/LoadingSpinner/LoadingSpinner";

import { getItemsFetching } from "selectors";

interface WaitForSyncProps {
  children?: ReactNode;
  requiredData: string[];
  size?: string;
  actionName?: string;
  subjectName?: string;
  showLoadingSpinner?: boolean;
  delay?: number;
  forceNotLoading?: boolean;
}

function WaitForSync({
  children,
  requiredData,
  size = undefined,
  actionName = "Loading",
  subjectName = null,
  showLoadingSpinner = true,
  delay = null,
  forceNotLoading = false,
}: WaitForSyncProps) {
  // isDelayed should be false if no delay is passed in, otherwise a value denotes to wait.
  const [isDelayed, setIsDelayed] = useState(delay !== null);
  const isLoading = useSelector(getItemsFetching(requiredData));

  // If we've been asked to delay, wait [delay] seconds then trigger the render.
  useEffect(() => {
    if (delay) {
      const timeOut = setTimeout(() => {
        setIsDelayed(false);
      }, delay);
      return () => clearTimeout(timeOut);
    }
    return () => null;
  }, [delay, setIsDelayed]);

  if (!forceNotLoading && (isLoading || isDelayed)) {
    if (showLoadingSpinner) {
      return (
        <CenteredGreedy>
          <LoadingSpinner
            size={size}
            actionName={actionName}
            subjectName={subjectName}
          />
        </CenteredGreedy>
      );
    }
    return null;
  }
  return children;
}

export default React.memo(
  WaitForSync,
  (prevProps, nextProps) =>
    // Deep check requiredData, shallow check the rest.
    isEqual(prevProps.requiredData, nextProps.requiredData) &&
    omit(prevProps, "requiredData") === omit(nextProps, "requiredData"),
);
